/**
 * Root application layout: Sidebar + main content area with Outlet.
 *
 * - Checks auth state on mount, redirects to /login if not authenticated
 * - Uses useSidebarStore for responsive margin (ml-60 expanded, ml-16 collapsed)
 * - Shows loading skeleton while checking auth
 */
import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useSidebarStore } from './stores/sidebar.store';
import { useAuthStore } from './hooks/use-auth';
import { useSidebarBadgeCounts } from './hooks/use-sidebar-badge-counts';
import { Sidebar } from './components/layout/sidebar';

export function AppLayout() {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const navigate = useNavigate();
  const badgeCounts = useSidebarBadgeCounts();

  useEffect(() => {
    checkAuth().then((ok) => {
      if (!ok) navigate('/login', { replace: true });
    });
  }, [checkAuth, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-surface-muted">
        <div className="h-8 w-8 animate-pulse rounded-full bg-border-soft" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex min-h-[100dvh]">
      <Sidebar badgeCounts={badgeCounts} />
      <main
        className={`flex-1 bg-surface-muted transition-[margin-left] duration-200 ease-out ${
          collapsed ? 'ml-16' : 'ml-60'
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
